import type { SchemaOrgNode } from '@core-mkt/services/schema/schema-org-node.model';

export interface TopSectionTrustpilotSchemaData {
  /** Absolute URL of the site root (e.g. https://www.aceable.com/) */
  siteUrl: string;
  /** Brand / organization display name (e.g. "Aceable") */
  organizationName: string;
  /** Trustpilot trust score (e.g. 4.8) */
  trustScore: number;
  /** Total number of reviews */
  reviewCount: number;
}

/**
 * Returns a Schema.org EducationalOrganization node with AggregateRating.
 * Returns `null` when Trustpilot data is unavailable — i.e. the organization
 * name is missing/blank, or trustScore/reviewCount are not positive finite
 * numbers. Guards against emitting a low-quality node with an empty `name`.
 */
export function getTopSectionTrustpilotSchema(data: TopSectionTrustpilotSchemaData | null): SchemaOrgNode | null {
  if (
    !data ||
    !data.siteUrl?.trim() ||
    !data.organizationName?.trim() ||
    !Number.isFinite(data.trustScore) ||
    data.trustScore <= 0 ||
    !Number.isFinite(data.reviewCount) ||
    data.reviewCount <= 0
  ) {
    return null;
  }

  const siteUrl = data.siteUrl.endsWith('/') ? data.siteUrl : `${data.siteUrl}/`;

  return {
    '@type': 'EducationalOrganization',
    '@id': `${siteUrl}#organization`,
    name: data.organizationName,
    url: siteUrl,
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: data.trustScore,
      bestRating: 5,
      reviewCount: data.reviewCount,
    },
  };
}
